/**
 * Shopify GraphQL Service
 * Makes authenticated GraphQL queries to Shopify Admin API using App Bridge
 * 
 * Uses authenticatedFetch from @shopify/app-bridge-utils for authenticated requests
 * This is the recommended way for React-only implementations
 * 
 * Reference: 
 * - https://shopify.dev/docs/api/app-bridge-library
 * - https://shopify.dev/docs/api/admin-graphql
 */

// Import authenticatedFetch from @shopify/app-bridge-utils
// This package is required for authenticated requests in embedded apps
import { authenticatedFetch } from '@shopify/app-bridge-utils';

// Shopify Admin API version - update as needed
const SHOPIFY_API_VERSION = '2025-07';

/**
 * Get shop domain from URL params
 * @returns {string|null} Shop domain or null
 */
function getShopDomain() {
  const urlParams = new URLSearchParams(window.location.search);
  const shop = urlParams.get('shop');
  
  if (!shop) {
    return null;
  }
  
  // Normalize shop domain
  return shop.includes('.myshopify.com') ? shop : `${shop}.myshopify.com`;
}

/**
 * Execute a GraphQL query using App Bridge authenticated fetch
 * 
 * @param {Object} appBridge - App Bridge instance from useSafeAppBridge hook
 * @param {string} query - GraphQL query string
 * @param {Object} variables - GraphQL variables (optional)
 * @returns {Promise<Object>} GraphQL response
 * @throws {Error} If appBridge is not available or request fails
 */
export async function graphQLRequest(appBridge, query, variables = {}) {
  // Input validation
  if (!query || typeof query !== 'string' || query.trim().length === 0) {
    throw new Error('GraphQL query is required');
  }

  if (!appBridge) {
    throw new Error('App Bridge instance is required. Make sure you are using useSafeAppBridge hook.');
  }

  // Validate authenticatedFetch is available
  if (!authenticatedFetch || typeof authenticatedFetch !== 'function') {
    throw new Error(
      'authenticatedFetch is not available. ' +
      'Please ensure @shopify/app-bridge-utils is installed: npm install @shopify/app-bridge-utils'
    );
  }

  try {
    // Get shop domain from URL params
    const shopDomain = getShopDomain();
    if (!shopDomain) {
      throw new Error('Shop parameter is required. Ensure app is loaded with shop query parameter.');
    }

    // Verify app is embedded (check for host parameter)
    const urlParams = new URLSearchParams(window.location.search);
    const host = urlParams.get('host');
    if (!host) {
      throw new Error(
        'App is not embedded in Shopify Admin. ' +
        'The host parameter is missing. Please access the app through Shopify Admin.'
      );
    }

    // Create authenticated fetch function using App Bridge
    // authenticatedFetch handles authentication automatically for embedded apps
    let authenticatedFetchFn;
    try {
      // Verify appBridge is valid before creating authenticated fetch
      if (!appBridge || typeof appBridge !== 'object') {
        throw new Error('Invalid App Bridge instance provided');
      }

      authenticatedFetchFn = authenticatedFetch(appBridge);
      if (!authenticatedFetchFn || typeof authenticatedFetchFn !== 'function') {
        throw new Error('authenticatedFetch did not return a valid fetch function');
      }
      
      // Verify it's not the global fetch (should be wrapped)
      if (authenticatedFetchFn === window.fetch) {
        console.warn('[Shopify GraphQL] authenticatedFetch returned global fetch - this may cause CORS issues');
      }

    } catch (fetchError) {
      console.error('[Shopify GraphQL] Failed to create authenticated fetch:', fetchError);
      throw new Error(`Failed to create authenticated fetch: ${fetchError.message}. Please ensure App Bridge is properly initialized.`);
    }

    // Prepare GraphQL request body
    const requestBody = {
      query: query.trim(),
      variables: variables || {},
    };

    // Use relative path - authenticatedFetch automatically resolves to shop domain
    // This is the correct approach according to Shopify documentation
    // authenticatedFetch handles domain resolution and adds session token
    const graphqlUrl = `/admin/api/${SHOPIFY_API_VERSION}/graphql.json`;

    // Make authenticated GraphQL request with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    try {
      // Use authenticatedFetch with relative URL
      // authenticatedFetch automatically:
      // 1. Resolves relative URL to the correct shop domain
      // 2. Adds session token header for authentication
      // 3. Handles CORS through App Bridge proxy
      const response = await authenticatedFetchFn(graphqlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Check if response is ok
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `GraphQL request failed with status ${response.status}`;
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // If not JSON, use the text as error message
          if (errorText && errorText.length < 200) {
            errorMessage = errorText;
          }
        }
        
        throw new Error(errorMessage);
      }

      // Parse JSON response
      const graphqlData = await response.json();

      // Check for GraphQL errors (separate from HTTP errors)
      if (graphqlData.errors && Array.isArray(graphqlData.errors) && graphqlData.errors.length > 0) {
        const errorMessages = graphqlData.errors
          .map(err => err.message || JSON.stringify(err))
          .join(', ');
        throw new Error(`GraphQL errors: ${errorMessages}`);
      }

      return graphqlData;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      // Handle specific error types
      if (fetchError.name === 'AbortError') {
        throw new Error('GraphQL request timed out after 30 seconds');
      }
      
      if (fetchError.message && (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('Cannot POST') || fetchError.name === 'TypeError')) {
        // Check if the error is about wrong domain
        if (fetchError.message.includes('Cannot POST')) {
          throw new Error(
            'GraphQL request failed: The request was sent to the wrong domain. ' +
            'This usually means the app is not properly embedded in Shopify Admin. ' +
            'Please ensure you are accessing the app through Shopify Admin (not directly). ' +
            'The URL should include ?shop=... and ?host=... parameters.'
          );
        }
        
        // CORS or network error
        const errorMsg = 
          'Network error: Failed to fetch products from Shopify. ' +
          '\n\nPossible causes:' +
          '\n1. App is not properly embedded in Shopify Admin' +
          '\n2. authenticatedFetch is not working correctly' +
          '\n3. Session token is missing or invalid' +
          '\n\nTroubleshooting:' +
          '\n- Ensure you are accessing the app through Shopify Admin' +
          '\n- Check that the URL includes ?shop=... and ?host=... parameters' +
          '\n- Verify App Bridge is properly initialized' +
          `\n- Shop: ${shopDomain}` +
          `\n- Host: ${host ? 'Present' : 'Missing'}`;
        
        throw new Error(errorMsg);
      }
      
      throw fetchError;
    }
  } catch (error) {
    // Enhanced error logging for debugging
    const shopDomain = getShopDomain();
    console.error('[Shopify GraphQL] Request failed:', {
      shop: shopDomain,
      query: query.substring(0, 100) + '...',
      variables,
      error: error.message,
      errorName: error.name,
    });
    throw error;
  }
}

/**
 * Get products from Shopify using GraphQL
 * Optimized query following Shopify GraphQL best practices
 * 
 * @param {Object} appBridge - App Bridge instance from useSafeAppBridge hook
 * @param {Object} options - Query options
 * @param {number} options.first - Number of products to fetch (default: 10, max: 250)
 * @param {string} options.after - Cursor for pagination
 * @param {string} options.query - Search query string (Shopify search syntax)
 * @returns {Promise<Object>} Products data with edges and pageInfo
 */
export async function getProducts(appBridge, options = {}) {
  const {
    first = 10,
    after = null,
    query = '',
  } = options;

  // Validate first parameter (Shopify limit: max 250)
  const validatedFirst = Math.min(Math.max(1, first), 250);

  // Optimized GraphQL query - only fetch necessary fields
  const graphqlQuery = `
    query GetProducts($first: Int!, $after: String, $query: String) {
      products(first: $first, after: $after, query: $query) {
        edges {
          node {
            id
            title
            handle
            status
            description
            featuredImage {
              id
              url
              altText
            }
            images(first: 5) {
              edges {
                node {
                  id
                  url
                  altText
                }
              }
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  price
                  sku
                  inventoryQuantity
                  availableForSale
                  image {
                    url
                  }
                }
              }
            }
            totalVariants
            createdAt
            updatedAt
          }
          cursor
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  `;

  const variables = {
    first: validatedFirst,
    ...(after && { after }),
    ...(query && { query }),
  };

  const response = await graphQLRequest(appBridge, graphqlQuery, variables);
  
  // Return standardized format
  if (response.data?.products) {
    return response.data.products;
  }
  
  // Fallback for different response formats
  return response.products || response;
}

/**
 * Get a single product by ID using GraphQL
 * 
 * @param {Object} appBridge - App Bridge instance from useSafeAppBridge hook
 * @param {string} productId - Product GID (e.g., "gid://shopify/Product/123456") or numeric ID
 * @returns {Promise<Object>} Product data
 * @throws {Error} If productId is invalid or product not found
 */
export async function getProductById(appBridge, productId) {
  if (!productId) {
    throw new Error('Product ID is required');
  }

  // Validate and normalize GID format
  let normalizedId = productId;
  if (!productId.startsWith('gid://shopify/Product/')) {
    // If numeric ID provided, convert to GID
    if (/^\d+$/.test(productId)) {
      normalizedId = `gid://shopify/Product/${productId}`;
    } else {
      throw new Error('Invalid product ID format. Expected GID (gid://shopify/Product/123) or numeric ID');
    }
  }

  const graphqlQuery = `
    query GetProduct($id: ID!) {
      product(id: $id) {
        id
        title
        handle
        status
        description
        descriptionHtml
        featuredImage {
          id
          url
          altText
        }
        images(first: 10) {
          edges {
            node {
              id
              url
              altText
            }
          }
        }
        variants(first: 50) {
          edges {
            node {
              id
              title
              price
              sku
              barcode
              inventoryQuantity
              availableForSale
              compareAtPrice
              image {
                url
              }
              inventoryItem {
                tracked
                inventoryPolicy
              }
            }
          }
        }
        totalVariants
        createdAt
        updatedAt
        tags
        productType
        vendor
      }
    }
  `;

  const variables = {
    id: normalizedId,
  };

  const response = await graphQLRequest(appBridge, graphqlQuery, variables);
  
  if (response.data?.product) {
    return response.data.product;
  }
  
  if (response.product) {
    return response.product;
  }
  
  throw new Error('Product not found or invalid response format');
}

/**
 * Search products by query string
 * Uses Shopify's search syntax
 * 
 * @param {Object} appBridge - App Bridge instance from useSafeAppBridge hook
 * @param {string} searchQuery - Search query using Shopify search syntax
 * @param {number} limit - Number of results (default: 10, max: 250)
 * @returns {Promise<Object>} Products data with edges and pageInfo
 */
export async function searchProducts(appBridge, searchQuery, limit = 10) {
  if (!searchQuery || typeof searchQuery !== 'string' || searchQuery.trim().length === 0) {
    throw new Error('Search query is required and must be a non-empty string');
  }

  return getProducts(appBridge, {
    first: limit,
    query: searchQuery.trim(),
  });
}

/**
 * Get existing metafield for a product
 * 
 * @param {Object} appBridge - App Bridge instance from useSafeAppBridge hook
 * @param {string} productId - Product GID (e.g., "gid://shopify/Product/123456")
 * @param {string} namespace - Metafield namespace (default: "custom")
 * @param {string} key - Metafield key (default: "campaign_data")
 * @returns {Promise<Object|null>} Metafield data or null if not found
 */
export async function getProductMetafield(appBridge, productId, namespace = 'custom', key = 'campaign_data') {
  if (!productId) {
    throw new Error('Product ID is required');
  }

  // Normalize product ID to GID format
  let normalizedId = productId;
  if (!productId.startsWith('gid://shopify/Product/')) {
    if (/^\d+$/.test(productId)) {
      normalizedId = `gid://shopify/Product/${productId}`;
    } else {
      throw new Error('Invalid product ID format');
    }
  }

  const graphqlQuery = `
    query GetProductMetafield($id: ID!, $namespace: String!, $key: String!) {
      product(id: $id) {
        id
        metafield(namespace: $namespace, key: $key) {
          id
          namespace
          key
          value
          type
        }
      }
    }
  `;

  const variables = {
    id: normalizedId,
    namespace,
    key,
  };

  try {
    const response = await graphQLRequest(appBridge, graphqlQuery, variables);
    return response.data?.product?.metafield || null;
  } catch (error) {
    console.error('Error getting product metafield:', error);
    return null;
  }
}

/**
 * Create or update a metafield on a product
 * According to Shopify docs: https://shopify.dev/docs/apps/build/custom-data#unstructured-metafields
 * 
 * @param {Object} appBridge - App Bridge instance from useSafeAppBridge hook
 * @param {string} productId - Product GID (e.g., "gid://shopify/Product/123456")
 * @param {Object} campaignData - Campaign data object to store in metafield
 * @param {string} namespace - Metafield namespace (default: "custom")
 * @param {string} key - Metafield key (default: "campaign_data")
 * @param {string} existingMetafieldId - Existing metafield ID if updating (optional)
 * @returns {Promise<Object>} Created/updated metafield data
 */
export async function setProductMetafield(appBridge, productId, campaignData, namespace = 'custom', key = 'campaign_data', existingMetafieldId = null) {
  if (!productId) {
    throw new Error('Product ID is required');
  }

  if (!campaignData) {
    throw new Error('Campaign data is required');
  }

  // Normalize product ID to GID format
  let normalizedId = productId;
  if (!productId.startsWith('gid://shopify/Product/')) {
    if (/^\d+$/.test(productId)) {
      normalizedId = `gid://shopify/Product/${productId}`;
    } else {
      throw new Error('Invalid product ID format');
    }
  }

  // Convert campaign data to JSON string
  const metafieldValue = JSON.stringify(campaignData);

  // If updating existing metafield, use metafieldsSet mutation
  if (existingMetafieldId) {
    const mutation = `
      mutation UpdateProductMetafield($metafields: [MetafieldsSetInput!]!) {
        metafieldsSet(metafields: $metafields) {
          metafields {
            id
            namespace
            key
            value
            type
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = {
      metafields: [
        {
          id: existingMetafieldId,
          value: metafieldValue,
          type: 'json',
        }
      ]
    };

    const response = await graphQLRequest(appBridge, mutation, variables);
    
    if (response.data?.metafieldsSet?.userErrors?.length > 0) {
      const errors = response.data.metafieldsSet.userErrors.map(e => e.message).join(', ');
      throw new Error(`Failed to update metafield: ${errors}`);
    }

    return response.data?.metafieldsSet?.metafields?.[0] || null;
  }

  // If creating new metafield, use productUpdate mutation
  const mutation = `
    mutation CreateProductMetafield($input: ProductInput!) {
      productUpdate(input: $input) {
        product {
          id
          metafield(namespace: "${namespace}", key: "${key}") {
            id
            namespace
            key
            value
            type
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const variables = {
    input: {
      id: normalizedId,
      metafields: [
        {
          namespace: namespace,
          key: key,
          value: metafieldValue,
          type: 'json',
        }
      ]
    }
  };

  const response = await graphQLRequest(appBridge, mutation, variables);
  
  if (response.data?.productUpdate?.userErrors?.length > 0) {
    const errors = response.data.productUpdate.userErrors.map(e => e.message).join(', ');
    throw new Error(`Failed to create metafield: ${errors}`);
  }

  return response.data?.productUpdate?.product?.metafield || null;
}

/**
 * Delete a metafield from a product
 * 
 * @param {Object} appBridge - App Bridge instance from useSafeAppBridge hook
 * @param {string} metafieldId - Metafield GID (e.g., "gid://shopify/Metafield/123456")
 * @returns {Promise<boolean>} True if deleted successfully
 */
export async function deleteProductMetafield(appBridge, metafieldId) {
  if (!metafieldId) {
    throw new Error('Metafield ID is required');
  }

  // Normalize metafield ID to GID format
  let normalizedId = metafieldId;
  if (!metafieldId.startsWith('gid://shopify/Metafield/')) {
    if (/^\d+$/.test(metafieldId)) {
      normalizedId = `gid://shopify/Metafield/${metafieldId}`;
    } else {
      throw new Error('Invalid metafield ID format');
    }
  }

  const mutation = `
    mutation DeleteMetafield($id: ID!) {
      metafieldDelete(id: $id) {
        deletedId
        userErrors {
          field
          message
        }
      }
    }
  `;

  const variables = {
    id: normalizedId,
  };

  try {
    const response = await graphQLRequest(appBridge, mutation, variables);
    
    if (response.data?.metafieldDelete?.userErrors?.length > 0) {
      const errors = response.data.metafieldDelete.userErrors.map(e => e.message).join(', ');
      throw new Error(`Failed to delete metafield: ${errors}`);
    }

    return !!response.data?.metafieldDelete?.deletedId;
  } catch (error) {
    console.error('Error deleting product metafield:', error);
    throw error;
  }
}

/**
 * Poll for MediaImage CDN URL until file is READY or timeout/failed.
 * @param {string} mediaImageId - MediaImage GID (e.g. gid://shopify/MediaImage/123)
 * @param {Function} graphqlCall - GraphQL client function
 * @param {{ pollIntervalMs?: number, timeoutMs?: number }} options - Poll interval and timeout in ms
 * @returns {Promise<string>} Permanent CDN URL (https://cdn.shopify.com/...)
 * @throws {Error} On timeout, FAILED status, or missing node
 */
async function pollMediaImageUrl(mediaImageId, graphqlCall, options = {}) {
  const pollIntervalMs = options.pollIntervalMs ?? 2000;
  const timeoutMs = options.timeoutMs ?? 30000;
  const start = Date.now();

  const nodeQuery = {
    query: `
      query getMediaImage($id: ID!) {
        node(id: $id) {
          ... on MediaImage {
            fileStatus
            image {
              url
            }
          }
        }
      }
    `,
    variables: { id: mediaImageId }
  };

  while (true) {
    const response = await graphqlCall(nodeQuery);
    if (response.errors && response.errors.length > 0) {
      const errMsg = response.errors.map(e => e.message).join(', ');
      throw new Error(`Failed to fetch MediaImage status: ${errMsg}`);
    }
    const node = response.data?.node;
    if (!node) {
      throw new Error('MediaImage node not found');
    }
    const fileStatus = node.fileStatus;
    const imageUrl = node.image?.url;

    if (fileStatus === 'FAILED') {
      throw new Error('Image processing failed.');
    }
    if (fileStatus === 'READY' && imageUrl) {
      return imageUrl;
    }
    if (Date.now() - start >= timeoutMs) {
      throw new Error('Image processing timed out; please try again.');
    }
    await new Promise(r => setTimeout(r, pollIntervalMs));
  }
}

/**
 * Upload an image file to Shopify using GraphQL
 * Process: stagedUploadsCreate -> upload file -> fileCreate -> (optional poll for CDN URL)
 * Returns permanent https://cdn.shopify.com URL.
 * Uses graphqlCall from Common.js (same pattern as bundle creation and metafield operations)
 *
 * @param {File} file - Image file to upload
 * @param {string} altText - Alt text for the image (optional)
 * @returns {Promise<Object>} { url, resourceUrl, altText, mediaImageId } - url is permanent CDN URL
 * @throws {Error} If upload fails
 */
export async function uploadImageToShopify(file, altText = '') {
  if (!file || !(file instanceof File)) {
    throw new Error('Valid file is required');
  }

  // Validate file type
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  // Check if App Bridge is available (window.shopify exists when app is embedded)
  if (!window.shopify) {
    throw new Error('App Bridge is not available. Please ensure you are accessing the app through Shopify Admin.');
  }

  // Import graphqlCall dynamically to avoid circular dependencies
  const { graphqlCall } = await import('../helper/Common');

  try {
    // Step 1: Create staged upload target using graphqlCall (same pattern as bundle/metafield operations)
    // Note: Shopify's default is PUT, but we'll use POST with FormData (more standard for multipart uploads)
    const stagedUploadQuery = {
      query: `
        mutation stagedUploadsCreate($input: [StagedUploadInput!]!) {
          stagedUploadsCreate(input: $input) {
            stagedTargets {
              resourceUrl
              url
              parameters {
                name
                value
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `,
      variables: {
        input: [
          {
            resource: 'IMAGE', // Use IMAGE resource type for image files (not FILE)
            filename: file.name,
            mimeType: file.type,
            fileSize: file.size.toString(), // Include file size for proper signature calculation
            httpMethod: 'POST', // Specify POST method to match our upload request
          }
        ]
      }
    };

    const stagedResponse = await graphqlCall(stagedUploadQuery);

    // Check for GraphQL errors
    if (stagedResponse.errors && stagedResponse.errors.length > 0) {
      const errors = stagedResponse.errors.map(e => e.message).join(', ');
      throw new Error(`Failed to create staged upload: ${errors}`);
    }

    if (stagedResponse.data?.stagedUploadsCreate?.userErrors?.length > 0) {
      const errors = stagedResponse.data.stagedUploadsCreate.userErrors.map(e => e.message).join(', ');
      throw new Error(`Failed to create staged upload: ${errors}`);
    }

    const stagedTarget = stagedResponse.data?.stagedUploadsCreate?.stagedTargets?.[0];
    if (!stagedTarget) {
      throw new Error('Failed to get staged upload target');
    }

    if (!stagedTarget.url || !stagedTarget.resourceUrl) {
      throw new Error('Staged upload target missing URL or resourceUrl');
    }

    if (!stagedTarget.parameters || !Array.isArray(stagedTarget.parameters) || stagedTarget.parameters.length === 0) {
      throw new Error('Staged upload target missing required parameters');
    }

    // Step 2: Upload file to Shopify storage
    // Note: Use regular fetch, not authenticatedFetch, because:
    // 1. stagedTarget.url is an absolute URL to Shopify's CDN (not a relative URL)
    // 2. Authentication is handled via FormData parameters, not headers
    // 3. authenticatedFetch is designed for relative URLs that resolve to shop domain
    const formData = new FormData();
    
    // Add parameters from staged upload response in the exact order provided
    // This is critical - parameters must be added before the file
    // The order matters for signature validation
    // DO NOT modify parameter names or values - use them exactly as Shopify provides
    stagedTarget.parameters.forEach(param => {
      if (!param.name || param.value === undefined || param.value === null) {
        console.warn('[uploadImageToShopify] Skipping invalid parameter:', param);
        return;
      }
      // Use exact parameter name and value from Shopify (critical for signature)
      formData.append(param.name, param.value);
    });
    
    // Add the file last (must be last according to Shopify docs)
    // The file field name should match what Shopify expects
    // For POST with FormData, the field name is typically 'file'
    formData.append('file', file);

    // Upload file directly to Shopify CDN using regular fetch
    // The staged upload URL is already authenticated via the parameters in FormData
    // Use POST method (must match httpMethod specified in mutation)
    // Important: Do NOT set any headers manually - let the browser set Content-Type with boundary
    try {
      const uploadResponse = await fetch(stagedTarget.url, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - browser will automatically set it with boundary for multipart/form-data
        // Setting it manually will break the signature validation
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`Failed to upload file: ${uploadResponse.status} - ${errorText.substring(0, 200)}`);
      }

      // Step 3: fileCreate to get permanent CDN URL (https://cdn.shopify.com/...)
      const fileCreateMutation = {
        query: `
          mutation fileCreate($files: [FileCreateInput!]!) {
            fileCreate(files: $files) {
              files {
                ... on MediaImage {
                  id
                  fileStatus
                  image {
                    url
                  }
                }
              }
              userErrors {
                field
                message
              }
            }
          }
        `,
        variables: {
          files: [
            {
              originalSource: stagedTarget.resourceUrl,
              alt: altText || undefined
            }
          ]
        }
      };
      const fileCreateResponse = await graphqlCall(fileCreateMutation);
      console.log('fileCreateResponse', fileCreateResponse);

      if (fileCreateResponse.errors && fileCreateResponse.errors.length > 0) {
        const errMsg = fileCreateResponse.errors.map(e => e.message).join(', ');
        throw new Error(`Failed to create permanent file for image: ${errMsg}`);
      }
      if (fileCreateResponse.data?.fileCreate?.userErrors?.length > 0) {
        const errMsg = fileCreateResponse.data.fileCreate.userErrors.map(e => e.message).join(', ');
        throw new Error(`Failed to create permanent file for image: ${errMsg}`);
      }

      const createdFiles = fileCreateResponse.data?.fileCreate?.files;
      const mediaImage = createdFiles?.[0];
      if (!mediaImage?.id) {
        throw new Error('Failed to create permanent file for image.');
      }
      if (mediaImage.fileStatus === 'FAILED') {
        throw new Error('Image processing failed.');
      }

      let cdnUrl = mediaImage.image?.url;
      if (!cdnUrl) {
        cdnUrl = await pollMediaImageUrl(mediaImage.id, graphqlCall, { pollIntervalMs: 2000, timeoutMs: 30000 });
      }

      const result = {
        url: cdnUrl,
        resourceUrl: cdnUrl,
        altText: altText || file.name,
        mediaImageId: mediaImage.id
      };
      console.log('[uploadImageToShopify] Upload success, CDN URL:', result);
      return result;
    } catch (uploadError) {
      console.error('[uploadImageToShopify] Upload error:', uploadError);
      // Re-throw with more context
      if (uploadError.message && uploadError.message.includes('SignatureDoesNotMatch')) {
        throw new Error(
          'File upload signature validation failed. ' +
          'This may be due to incorrect parameter formatting. ' +
          'Please ensure you are accessing the app through Shopify Admin and try again.'
        );
      }
      throw uploadError;
    }
  } catch (error) {
    console.error('Error uploading image to Shopify:', error);
    throw error;
  }
}

